import React, { useEffect, useRef, useState } from 'react';
import { feedbacksApi, FeedbackMessage } from '../../api/feedbacksApi';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../contexts/LangContext';
import { LocalizedFileInput } from '../common/LocalizedFileInput';

type MessageChannel = 'resident' | 'internal';

interface FeedbackMessagePanelProps {
  feedbackId: string;
  disabled?: boolean;
  showHeader?: boolean;
  enablePolling?: boolean;
  onMessagesChange?: () => void;
  renderAttachment?: (fileName: string, index: number) => React.ReactNode;
}

export const FeedbackMessagePanel: React.FC<FeedbackMessagePanelProps> = ({
  feedbackId,
  disabled = false,
  showHeader = true,
  enablePolling = false,
  onMessagesChange,
  renderAttachment,
}) => {
  const { t } = useLang();
  const { user } = useAuth();
  const isStaff = user?.role !== 'RESIDENT';
  const [activeChannel, setActiveChannel] = useState<MessageChannel>('resident');
  const [messages, setMessages] = useState<FeedbackMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadMessages = async (channel: MessageChannel = activeChannel) => {
    if (!feedbackId) return;
    setLoading(true);
    try {
      const data = await feedbacksApi.getFeedbackMessages(feedbackId, channel);
      setMessages(data);
    } catch (error) {
      console.error('Error loading feedback messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isStaff) {
      setActiveChannel('resident');
    }
  }, [isStaff]);

  useEffect(() => {
    loadMessages(activeChannel);
  }, [feedbackId, activeChannel]);

  useEffect(() => {
    if (!enablePolling || !feedbackId) return undefined;
    const timer = window.setInterval(() => {
      loadMessages(activeChannel);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [enablePolling, feedbackId, activeChannel]);

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || sending || disabled) return;

    setSending(true);
    try {
      await feedbacksApi.addFeedbackMessage(
        feedbackId,
        {
          content: newMessage.trim(),
          isInternal: isStaff && activeChannel === 'internal',
        },
        selectedFiles,
      );
      setNewMessage('');
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadMessages(activeChannel);
      onMessagesChange?.();
    } catch (error) {
      console.error('Error sending feedback message:', error);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('vi-VN');

  const defaultRenderAttachment = (fileName: string, index: number) => (
    <a
      key={`${fileName}-${index}`}
      href={fileName}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline text-sm"
    >
      {fileName.split('/').pop()}
    </a>
  );

  return (
    <div>
      {showHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{t('feedbackExchange')}</h3>
          <button
            type="button"
            onClick={() => loadMessages(activeChannel)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {t('feedbackRefresh')}
          </button>
        </div>
      )}

      {isStaff && (
        <div className="flex border-b border-gray-200 mb-4">
          <button
            type="button"
            onClick={() => setActiveChannel('resident')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeChannel === 'resident'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('feedbackResidentChat')}
          </button>
          <button
            type="button"
            onClick={() => setActiveChannel('internal')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeChannel === 'internal'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('feedbackInternalChat')}
          </button>
        </div>
      )}

      {activeChannel === 'internal' && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t('feedbackInternalChatHint')}
        </div>
      )}

      <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="text-center text-gray-500 py-8">{t('loading')}...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">{t('noFeedbackMessages')}</div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className="flex space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {message.createdBy.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900">{message.createdBy.name}</span>
                  <span className="text-gray-500 text-sm">{formatDate(message.createdAt)}</span>
                </div>
                <div className="mt-1 text-gray-800">{message.content}</div>
                {message.attachments?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {message.attachments.map((attachment, index) =>
                      (renderAttachment || defaultRenderAttachment)(attachment, index),
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="border-t pt-4">
        <div className="flex space-x-3 items-end">
          <div className="flex-1">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={t('feedbackMessagePlaceholder')}
              rows={3}
              disabled={disabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <LocalizedFileInput
                files={selectedFiles}
                onChange={setSelectedFiles}
                inputRef={fileInputRef}
                disabled={disabled}
                chooseFilesLabel={t('chooseFiles')}
                noFileChosenLabel={t('noFileChosen')}
              />
            </div>
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending || disabled}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? t('loading') : t('Send message')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
